import React, { useContext } from "react";
import { Box, IconButton, Popover } from "@mui/material";
import { PromptsItem } from "../../types";
import PublicIcon from "@mui/icons-material/Public";
import LockIcon from "@mui/icons-material/Lock";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import styles from "./PromptsCard.module.scss";
import { deletePrompt, updatePrompt } from "../../store/PromptActions";
import { useAxios } from "myAxios";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import { ICON_OPTIONS } from "../../constants";
import { Lightbulb } from "@mui/icons-material";
import { useAppActions } from "../../store/AppStore";
import { useModel } from "hooks/useModel";
import { Cookies } from "utils";
import { EMAILID_COOKIE } from "constant";
import { AuthProvider } from "pages";
import { FEATURE_KEY } from "constant";

export const getIconComponent = (id: string) => {
  const iconOption = ICON_OPTIONS.find((icon) => icon.id === id);
  const IconComponent = iconOption?.icon;
  return IconComponent ? (
    <IconComponent
      fontSize="inherit"
      sx={{ color: iconOption.color }}
    />
  ) : (
    <Lightbulb
      fontSize="inherit"
      sx={{ color: "#F59E0B" }}
    />
  );
};

const PromptsCardItem = ({ item }: { item: any }) => {
  const axios = useAxios();
  const { updatePromptFn, sendMessageFn } = useAppActions(axios);
  const { setPromptConfig, currentAccountTags } = useModel("aichat");
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const emailId = Cookies.get(EMAILID_COOKIE);
  const authDetail = useContext(AuthProvider);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const toggleFavorite = async (id: string, item: any) => {
    const data = {
      favorite: !item.favorite,
      promptContent: item.promptContent,
      promptTitle: item.promptTitle,
      promptType: item.promptType,
      promptVisibility: item.promptVisibility,
      promptIcon: item.promptIcon,
      promptDescription: item.promptDescription,
    };
    updatePromptFn(id, { ...item, ...data }, "favorite");
    try {
      await updatePrompt(axios, id, data);
    } catch (error) {
      toast.error(JSON.stringify(error) || "Failed to update favorite status");
      updatePromptFn(id, item, "favorite");
    }
  };

  return (
    <>
      <Box
        key={item.id}
        className={styles.promptItem}
      >
        <Box className={styles.promptTitle}>
          <div className={styles.icon}>{getIconComponent(item.promptIcon)}</div>
          <div className={styles.promptName}>{item.promptTitle}</div>
          <span className={styles.isFavorite}>
            <IconButton onClick={() => debounce(() => toggleFavorite(item.id, item), 300)()}>
              <img
                src={item.favorite ? "/aichat/favorite.svg" : "/aichat/nofavorite.svg"}
                alt=""
              />
            </IconButton>
          </span>
        </Box>
        <Box className={styles.promptDescription}>{item.promptContent}</Box>
        <Box className={styles.promptInfo}>
          <div className={styles.promptInfoItem}>
            <span>
              {item.promptVisibility === "public" ? (
                <>
                  <PublicIcon fontSize="inherit" /> Public
                </>
              ) : (
                <>
                  <LockIcon fontSize="inherit" /> Private
                </>
              )}
            </span>
            <span>By {item.promptType === "system" ? "SYSTEM" : item.creator || "myself"}</span>
          </div>
          <div className={styles.promptInfoItem}>
            <IconButton
              size="small"
              onClick={handleClick}
            >
              <MoreHorizRoundedIcon fontSize="small" />
            </IconButton>

            <div
              className={styles.sendButton}
              onClick={() =>
                sendMessageFn(item.promptContent, undefined, currentAccountTags, {
                  description: item.promptDescription,
                  title: item.promptTitle,
                  prompt: item.promptContent,
                })
              }
            ></div>
          </div>
        </Box>
      </Box>
      <Popover
        className={styles.promptsCardPopover}
        open={open}
        id={item.id}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 2,
        }}
      >
        <div className={styles.operateMenus}>
          {(authDetail?.features?.includes(FEATURE_KEY.AI_CHAT_SYSTEM_PROMPT) &&
            item.promptType === "system") ||
          emailId === item.createdBy ||
          !item?.createdBy ? (
            <div
              className={styles.menuItem}
              onClick={() =>
                setPromptConfig({
                  ...item,
                  state: "Edit",
                })
              }
            >
              Edit
            </div>
          ) : (
            <div
              className={styles.menuItem}
              onClick={() =>
                setPromptConfig({
                  ...item,
                  state: "View",
                })
              }
            >
              Details
            </div>
          )}
          <div
            className={styles.menuItem}
            onClick={() =>
              setPromptConfig({
                ...item,
                state: "New",
                promptTitle: item.promptTitle + " Copy",
                id: null,
                createdBy: undefined,
                creator: undefined,
              })
            }
          >
            Duplicate
          </div>
          {((authDetail?.features?.includes(FEATURE_KEY.AI_CHAT_SYSTEM_PROMPT) &&
            item.promptType === "system") ||
            emailId === item.createdBy ||
            !item?.createdBy) && (
            <div
              className={styles.menuItem}
              onClick={() => {
                updatePromptFn(item.id, {}, "delete");
                deletePrompt(axios, item.id).catch((e) => {
                  toast.error(e?.message || "Failed to delete prompt. Please try again.");
                  updatePromptFn(item.id, item, "add");
                });
              }}
            >
              Delete
            </div>
          )}
        </div>
      </Popover>
    </>
  );
};

const PromptsCardList = (props: { data: PromptsItem }) => {
  const { data } = props;

  return (
    <>
      {data?.list.map((item, index) => (
        <PromptsCardItem
          key={item.id + index}
          item={item}
        />
      ))}
    </>
  );
};

export default PromptsCardList;
